import { RANK_TIERS, NAME_MAP, IR_TENOR_YEARS } from '../constants';

// ── Field-name normalization ──────────────────────────────────────────────────
export function toNum(v) { const n = parseFloat(v); return isNaN(n) ? null : n; }

// Returns the timestamp of the most recent NYSE market close (4pm ET, weekdays only)
export function lastMarketClose() {
  const now = Date.now();
  const etStr  = new Date(now).toLocaleString("en-US", { timeZone:"America/New_York" });
  const etNow  = new Date(etStr);
  const close  = new Date(etStr);
  close.setHours(16, 0, 0, 0);
  if (etNow < close) close.setDate(close.getDate() - 1);
  while (close.getDay() === 0 || close.getDay() === 6) close.setDate(close.getDate() - 1);
  const etOffset = etNow.getTime() - now;
  return close.getTime() - etOffset;
}

export function pick(obj, ...keys) {
  for (const k of keys) { if (obj != null && obj[k] != null && obj[k] !== "") return obj[k]; }
  return null;
}

export function normalizeClaudeData(d) {
  if (!d || typeof d !== "object") return d;

  d.compositeFairValue = toNum(pick(d,"compositeFairValue","weightedFairValue","fairValue","targetPrice","intrinsicValue","avgFairValue"));
  d.fairValueLow       = toNum(pick(d,"fairValueLow","fairValueBear","bearCaseFairValue","lowFairValue")) ?? null;
  d.fairValueHigh      = toNum(pick(d,"fairValueHigh","fairValueBull","bullCaseFairValue","highFairValue")) ?? null;
  d.dollarUpside       = toNum(pick(d,"dollarUpside","upside","upsideDownside","priceDifference","upsidePotential"));
  d.percentUpside      = toNum(pick(d,"percentUpside","percentageUpside","pctUpside","upsidePercent","potentialUpside"));
  if (d.dollarUpside == null && d.compositeFairValue != null && d.currentPrice != null && d.currentPrice > 0) {
    d.dollarUpside  = d.compositeFairValue - d.currentPrice;
    d.percentUpside = (d.dollarUpside / d.currentPrice) * 100;
  }
  d.analystTarget     = toNum(pick(d,"analystTarget","streetTarget","analystConsensus","consensusTarget","ptConsensus")) ?? null;
  d.analystTargetHigh = toNum(pick(d,"analystTargetHigh","ptHigh","targetHigh")) ?? null;
  d.analystTargetLow  = toNum(pick(d,"analystTargetLow","ptLow","targetLow"))   ?? null;

  // Catalysts
  const catRaw = pick(d,"catalysts","keyDrivers","keyFactors","drivers","valuationDrivers","insights","investmentCatalysts");
  const catArr = Array.isArray(catRaw) ? catRaw : (Array.isArray(d.catalysts) ? d.catalysts : []);
  d.catalysts = catArr.map(c => {
    if (typeof c === "string") return { title:c, description:c, impact:"neutral" };
    const ri = (pick(c,"impact","sentiment","type","direction") || "neutral").toString().toLowerCase();
    return {
      title:       pick(c,"title","name","driver","factor","heading","header","subject","catalystName") ?? "",
      description: pick(c,"description","detail","summary","body","text","content","explanation","rationale") ?? "",
      impact:      ri.includes("pos")||ri.includes("bull") ? "positive" : ri.includes("neg")||ri.includes("bear") ? "negative" : "neutral",
      dollarImpact:pick(c,"dollarImpact","impact_amount","valueImpact","priceImpact") ?? null,
    };
  });

  // Peers
  const peerRaw = pick(d,"peers","peerComparison","competitors","peerStocks","comparables","peerAnalysis","peerCompanies");
  const peerArr = Array.isArray(peerRaw) ? peerRaw : (Array.isArray(d.peers) ? d.peers : []);
  d.peers = peerArr.map(p => {
    const fv = toNum(pick(p,"fairValue","intrinsicValue","targetPrice","estimatedFairValue","value"));
    const cp = toNum(pick(p,"currentPrice","price","current","stockPrice","marketPrice","lastPrice","tradingPrice"));
    const du = toNum(pick(p,"dollarUpside","upside","upsideDownside","priceDifference")) ?? (fv!=null&&cp!=null ? fv-cp : null);
    const pu = toNum(pick(p,"percentUpside","percentageUpside","pctUpside","upsidePercent")) ?? (du!=null&&cp ? (du/cp)*100 : null);
    return { ...p,
      ticker:       pick(p,"ticker","symbol","stockTicker") ?? p.ticker ?? "",
      name:         pick(p,"name","companyName","company","fullName") ?? p.name ?? "",
      currentPrice: cp, fairValue: fv, dollarUpside: du, percentUpside: pu,
      grade:        pick(p,"grade","rating","letterGrade") ?? p.grade ?? null,
    };
  });

  // Industry context
  const indRaw = pick(d,"industryContext","industryBullets","industry","industryInsights","sectorContext","sectorAnalysis","marketContext","industryTrends");
  const indArr = Array.isArray(indRaw) ? indRaw : (Array.isArray(d.industryContext) ? d.industryContext : []);
  d.industryContext = indArr.map(item => {
    if (typeof item === "string") return { bullet:item, sentiment:"neutral" };
    const rs = (pick(item,"sentiment","impact","direction","tone") || "neutral").toString().toLowerCase();
    return {
      bullet:    pick(item,"bullet","text","description","point","insight","content","detail","observation") ?? "",
      sentiment: rs.includes("pos")||rs.includes("bull") ? "positive" : rs.includes("neg")||rs.includes("bear") ? "negative" : "neutral",
    };
  });

  // Fundamentals
  const f = d.fundamentals || {};
  d.fundamentals = {
    eps:               toNum(pick(f,"eps")) ?? toNum(pick(d,"eps")) ?? null,
    peRatioTTM:        toNum(pick(f,"peRatioTTM","peRatio","pe")) ?? toNum(pick(d,"peRatioTTM","peRatio","pe")) ?? null,
    industryPE:        toNum(pick(f,"industryPE","industryAvgPE","sectorPE","avgPE")) ?? toNum(pick(d,"industryPE","industryAvgPE","sectorPE")) ?? null,
    beta:              toNum(pick(f,"beta")) ?? toNum(pick(d,"beta")) ?? null,
    revenueGrowthRate: toNum(pick(f,"revenueGrowthRate","revenueGrowth")) ?? toNum(pick(d,"revenueGrowthRate","revenueGrowth")) ?? null,
    wacc:              toNum(pick(f,"wacc")) ?? toNum(pick(d,"wacc")) ?? null,
    dividendPerShare:  toNum(pick(f,"dividendPerShare","dps","dividend")) ?? toNum(pick(d,"dividendPerShare","dps")) ?? null,
    dividendGrowthRate:toNum(pick(f,"dividendGrowthRate","divGrowth")) ?? toNum(pick(d,"dividendGrowthRate","divGrowth")) ?? null,
  };

  d.overallConfidence = pick(d,"overallConfidence","confidence","dataQuality","confidenceLevel") ?? null;
  if (d.overallConfidence) {
    const cl = d.overallConfidence.toString().toLowerCase();
    d.overallConfidence = cl.includes("high") ? "high" : cl.includes("low") ? "low" : "medium";
  }

  d.grade = pick(d,"grade","overallGrade","valuationGrade","letterGrade") ?? d.grade ?? "C";
  d.score = toNum(pick(d,"score","overallScore","valuationScore")) ?? ({
    "A+":97,"A":88,"B+":78,"B":67,"C":50,"D":33,"F":18
  }[d.grade] ?? 50);
  d.tier = pick(d,"tier","companyTier","stockTier","category") ?? d.tier ?? "mature";

  if (Array.isArray(d.models)) {
    d.models = d.models.map(m => ({
      ...m,
      name:        pick(m,"name","modelName","model") ?? "",
      fairValue:   toNum(pick(m,"fairValue","value","price","targetPrice","estimatedValue")) ?? null,
      weight:      toNum(pick(m,"weight","weighting","modelWeight")) ?? null,
      included:    m.included ?? m.applicable ?? (m.excluded !== true && m.notApplicable !== true),
      reliability: pick(m,"reliability","confidence","confidenceLevel","accuracy") ?? "medium",
      formula:     pick(m,"formula","explanation","formulaDescription","calculation") ?? "",
      note:        pick(m,"note","exclusionReason","reason","caveat") ?? "",
    }));
  }

  return d;
}

// ── Rank helper ───────────────────────────────────────────────────────────────
export function getRank(pts) {
  let t = RANK_TIERS[0];
  for (const r of RANK_TIERS) { if (pts >= r.min) t = r; }
  const idx = RANK_TIERS.indexOf(t);
  const next = RANK_TIERS[idx + 1] || null;
  return { ...t, next, progress: next ? Math.round(((pts - t.min) / (next.min - t.min)) * 100) : 100, ptsToNext: next ? next.min - pts : 0 };
}

// ── Model name normalizer ─────────────────────────────────────────────────────
export function normName(n) { return NAME_MAP[n] || n; }

// ── Note plainifier ───────────────────────────────────────────────────────────
export function plainifyNote(note) {
  if (!note) return null;
  return note
    .replace(/not applicable/gi, "doesn't apply here")
    .replace(/dividend discount model/gi, "dividend model")
    .replace(/no dividends?/gi, "this company doesn't pay dividends")
    .replace(/negative (eps|earnings)/gi, "the company isn't profitable right now")
    .replace(/negative book value/gi, "the company has more debt than assets on paper")
    .replace(/hypergrowth only/gi, "only used for very fast-growing companies")
    .replace(/mature.*only/gi, "only used for stable, established companies")
    .replace(/n\/a/gi, "not used here");
}

// ── Game helpers ──────────────────────────────────────────────────────────────
export function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; }
  return a;
}

// ── Portfolio helpers ─────────────────────────────────────────────────────────
export function pfCorrelation(r1, r2) {
  const n = Math.min(r1.length, r2.length);
  if (n < 3) return 0.2;
  const a = r1.slice(-n), b = r2.slice(-n);
  const ma = a.reduce((s,v)=>s+v,0)/n, mb = b.reduce((s,v)=>s+v,0)/n;
  const cov = a.reduce((s,v,i)=>s+(v-ma)*(b[i]-mb),0)/(n-1);
  const sa  = Math.sqrt(a.reduce((s,v)=>s+(v-ma)**2,0)/(n-1));
  const sb  = Math.sqrt(b.reduce((s,v)=>s+(v-mb)**2,0)/(n-1));
  return sa>0&&sb>0 ? Math.max(-1,Math.min(1,cov/(sa*sb))) : 0;
}

export function computeFrontier(assets, riskFree=0.043) {
  const n = assets.length;
  const cov = Array.from({length:n},(_,i)=>Array.from({length:n},(_,j)=>{
    const vi = assets[i].annualVol||0.20, vj = assets[j].annualVol||0.20;
    if (i===j) return vi*vi;
    const corr = pfCorrelation(assets[i].monthlyReturns||[], assets[j].monthlyReturns||[]);
    return corr*vi*vj;
  }));

  const pVol = w => {
    let v=0;
    for(let i=0;i<n;i++) for(let j=0;j<n;j++) v+=w[i]*w[j]*cov[i][j];
    return Math.sqrt(Math.max(0,v));
  };
  const pRet = w => w.reduce((s,wi,i)=>s+wi*(assets[i].annualReturn??0.06),0);

  // Current portfolio
  const cw = assets.map(a=>a.weight);
  const curRet=pRet(cw), curVol=pVol(cw);
  const curSharpe = curVol>0?(curRet-riskFree)/curVol:0;

  // 500 random portfolios
  const portfolios = Array.from({length:500},()=>{
    const raw=Array.from({length:n},()=>Math.random());
    const sum=raw.reduce((a,b)=>a+b,0);
    const w=raw.map(v=>v/sum);
    const ret=pRet(w), vol=pVol(w), sharpe=vol>0?(ret-riskFree)/vol:0;
    return {w,ret,vol,sharpe};
  });

  const maxSharpe = portfolios.reduce((b,p)=>p.sharpe>b.sharpe?p:b);
  const minVol    = portfolios.reduce((b,p)=>p.vol<b.vol?p:b);

  return { portfolios, maxSharpe, minVol, current:{ret:curRet,vol:curVol,sharpe:curSharpe} };
}

// ── IR helpers ────────────────────────────────────────────────────────────────
export function irDetectShape(curve, origCurve) {
  if (!curve?.length) return "Normal";
  const TYRS = {"1M":1/12,"3M":0.25,"6M":0.5,"1Y":1,"2Y":2,"5Y":5,"7Y":7,"10Y":10,"20Y":20,"30Y":30};
  const sort = arr => [...arr].sort((a,b)=>(TYRS[a.tenor]||0)-(TYRS[b.tenor]||0));
  const sorted = sort(curve);
  if (sorted.length < 2) return "Normal";
  const rShort = sorted[0].rate, rLong = sorted[sorted.length-1].rate;
  const spread = rLong - rShort;
  if (!origCurve) {
    if (spread < -0.25) return "Inverted";
    if (Math.abs(spread) < 0.25) return "Flat";
    return "Normal";
  }
  const os = sort(origCurve);
  const oShort = os[0]?.rate ?? rShort, oLong = os[os.length-1]?.rate ?? rLong;
  const spreadDelta = spread - (oLong - oShort);
  const avgNow  = curve.reduce((s,c)=>s+c.rate,0)/curve.length;
  const avgOrig = origCurve.reduce((s,c)=>s+c.rate,0)/origCurve.length;
  const lvl = avgNow - avgOrig;
  if (spread < -0.25) return "Inverted";
  if (Math.abs(spread) < 0.25) return lvl < -0.05 ? "Bull Flattener" : lvl > 0.05 ? "Bear Flattener" : "Flat";
  if (spreadDelta > 0.1) return lvl < -0.05 ? "Bull Steepener" : "Bear Steepener";
  if (spreadDelta < -0.1) return lvl < -0.05 ? "Bull Flattener" : "Bear Flattener";
  return "Normal";
}

export function irForwardRates(curve) {
  const get = t => { const c = curve.find(x => x.tenor === t); return c ? c.rate / 100 : null; };
  const yr = t => IR_TENOR_YEARS[t];
  const fwd = (t1, t2) => {
    const r1 = get(t1), r2 = get(t2);
    if (!r1 || !r2 || yr(t2) <= yr(t1)) return null;
    const v = Math.pow(Math.pow(1+r2, yr(t2)) / Math.pow(1+r1, yr(t1)), 1/(yr(t2)-yr(t1))) - 1;
    return isFinite(v) ? parseFloat((v*100).toFixed(2)) : null;
  };
  return [
    {label:"1-year rate, 1 year from now", short:"1Y1Y", value:fwd("1Y","2Y"), explain:"What bond markets imply the 1-year Treasury will be in 12 months."},
    {label:"5-year rate, 5 years from now", short:"5Y5Y", value:fwd("5Y","10Y"), explain:"The most-watched forward rate by central banks. Below 2% signals prolonged low growth."},
    {label:"10-year rate, 20 years from now", short:"20Y10Y", value:fwd("20Y","30Y"), explain:"Markets' best estimate of the neutral cost of capital decades into the future."},
  ];
}
